/**
 * PromoCodeDisclosure — the promo-code entry disclosure both member clients
 * mount beside the money it changes (web checkout steps, mobile confirm page).
 *
 * NativeWebFirst throughout: a bare `<details>` whose open state lives on the
 * element (no `open` prop — `defaultOpen` is read once; imperative re-opens go
 * through the `ref` from an event handler), and an UNCONTROLLED `<form>` read
 * via `FormData` on submit — no per-keystroke state, Enter and the mobile
 * keyboard's Go for free (the marketing-site ContactForm precedent). The form
 * is keyed on the capture so a clear or auto-drop resets the input instead of
 * leaving a dead code one tap from re-applying.
 *
 * Capture wiring stays per app (web: the sessionStorage-backed capture hook;
 * mobile: page state). Notes carry `data-variant` / `data-reason` and stable
 * testids as the e2e hooks — specs never match member copy.
 */

import { useState } from 'react'
import { Input } from '#primitives/inputs'
import { PromoNotice } from './PromoNotice'
import type { PromoCodeDisclosureProps } from './types'

export function PromoCodeDisclosure({
  ref,
  defaultOpen = false,
  promoCode,
  applied,
  refusal,
  summaryLabel = 'Promo Code',
  onApply,
  onRemove,
}: PromoCodeDisclosureProps) {
  // One-shot: computed on first render only, so the member can still toggle
  // freely afterwards (a changing `open` prop would fight the native element).
  const [initiallyOpen] = useState(defaultOpen)

  return (
    <details ref={ref} open={initiallyOpen || undefined} data-testid="promo-details">
      <summary className="cursor-pointer text-sm font-medium text-theme-600 text-interactive:text-theme-700">
        {summaryLabel}
      </summary>
      <div className="mt-3">
        {applied ? (
          <PromoNotice variant="applied" testId="promo-applied-note">
            <span className="flex items-center justify-between gap-3">
              <span>
                <b>{applied.code}</b> applied
                {applied.label ? ` — ${applied.label}` : ''}.
              </span>
              <button
                type="button"
                data-testid="promo-remove"
                onClick={onRemove}
                aria-label="Remove promo code"
                className="shrink-0 font-semibold underline text-interactive:text-green-900"
              >
                Remove
              </button>
            </span>
          </PromoNotice>
        ) : (
          <>
            <form
              key={promoCode ?? ''}
              data-testid="promo-form"
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                const code = String(new FormData(e.currentTarget).get('promoCode') ?? '').trim()
                // A blank (whitespace) submit CLEARS a captured code — it is
                // the only way to drop a REFUSED code, since Remove renders
                // only in the applied state.
                if (code) onApply(code)
                else if (promoCode) onRemove()
              }}
            >
              <Input
                type="text"
                name="promoCode"
                defaultValue={promoCode ?? ''}
                required
                enterKeyHint="go"
                placeholder="Promo code"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                data-testid="promo-input"
                className="h-11 flex-1"
              />
              <button
                type="submit"
                data-testid="promo-apply"
                className="shrink-0 rounded-full bg-theme-700 px-5 text-sm font-semibold text-white text-interactive:bg-theme-800"
              >
                Apply
              </button>
            </form>
            {refusal && (
              <div className="mt-2">
                <PromoNotice variant="refused" reason={refusal.reason} testId="promo-refused-note">
                  {refusal.message}
                </PromoNotice>
              </div>
            )}
          </>
        )}
      </div>
    </details>
  )
}
